import React from "react";
import CardTitle from "../../commons/CardTitle";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { getDateTime } from "../../helpers/dateTime";
import { useNavigate } from "react-router-dom";
import OrderItemDetails from "./OrderItemDetails";
import { formatNumber } from "../../utils";
import { TbTruckDelivery, TbDiscount2 } from "react-icons/tb";
import { BsClock, BsBoxSeam } from "react-icons/bs";

function AccountOrderDetails({ onBackClick, orderGroup, onInitiateClick }) {
  const navigate = useNavigate();
  const shippingAddress = JSON.parse(
    orderGroup?.shippingAddress ?? '{ "address": "" }'
  );

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'cancelled':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="shadow-md rounded-lg w-[70%] max-lg:w-full bg-white p-6 text-left">
      <div className="flex justify-between items-start mb-6">
        <div className="">
          <CardTitle title="Order Details" />
        </div>
        <div
          onClick={onBackClick}
          className="flex gap-2 text-[16px] max-lg:text-[14px] max-md:text-[12px] font-[600] items-center text-[#318EC9] cursor-pointer"
        >
          <div>
            <MdOutlineArrowBackIos />
          </div>
          <div>Back</div>
        </div>
      </div>

      {/* Order Status Timeline */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BsBoxSeam className="text-xl" />
            <span className="text-[14px] font-semibold">Order #{orderGroup?.orderNumber}</span>
          </div>
          <div className={`flex items-center gap-2 font-semibold ${getStatusColor(orderGroup?.status)}`}>
            <BsClock />
            <span className="capitalize">{orderGroup?.status}</span>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <TbTruckDelivery className="text-xl text-[#FF9D21]" />
          <span className="text-[14px] text-gray-600">
            Shipping Status: <span className="capitalize">{orderGroup?.shippingStatus}</span>
          </span>
        </div>
        {orderGroup?.couponCode && (
          <div className="mt-4 flex items-center gap-2 text-green-600">
            <TbDiscount2 className="text-xl" />
            <span className="text-[14px]">
              Coupon Applied: {orderGroup?.couponCode} 
              ({orderGroup?.couponType === 'fixed' ? 
                `₦${formatNumber(orderGroup?.couponDiscount)}` : 
                `${orderGroup?.Coupon?.value}% off`})
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Order Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-[16px] mb-4">Order Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between text-[14px]">
              <span className="text-gray-600">Order Date</span>
              <span>{getDateTime(orderGroup?.createdAt)}</span>
            </div>
            <div className="flex justify-between text-[14px]">
              <span className="text-gray-600">Payment Method</span>
              <span>{orderGroup?.paymentMethod}</span>
            </div>
            {orderGroup?.shipmentId && (
              <div className="flex justify-between text-[14px]">
                <span className="text-gray-600">Shipment ID</span>
                <span>{orderGroup?.shipmentId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-[16px] mb-4">Delivery Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between text-[14px]">
              <span className="text-gray-600">Recipient</span>
              <span>{shippingAddress?.firstName} {shippingAddress?.lastName}</span>
            </div>
            <div className="flex justify-between text-[14px]">
              <span className="text-gray-600">Address</span>
              <span className="text-right">{shippingAddress?.address}</span>
            </div>
            <div className="flex justify-between text-[14px]">
              <span className="text-gray-600">Phone</span>
              <span>{shippingAddress?.phoneNumber}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="mb-6">
        <h3 className="font-semibold text-[16px] mb-4">
          Order Items ({orderGroup?.orders?.length})
        </h3>
        <div className="space-y-4">
          {orderGroup?.orders?.map((orderItem) => (
            <OrderItemDetails
              key={orderItem.id}
              orderItem={orderItem}
              onViewClick={() => {
                navigate(`/products/${orderItem?.Product?.slug}`);
                navigate(0);
              }}
              onInitiateClick={onInitiateClick}
            />
          ))}
        </div>
      </div>

      {/* Order Summary */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold text-[16px] mb-4">Order Summary</h3>
        <div className="space-y-3">
          {orderGroup?.couponDiscount && (
            <div className="flex justify-between text-[14px]">
              <span className="text-gray-600">Subtotal (Before Discount)</span>
              <span>₦{formatNumber(orderGroup?.subTotalBeforeDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between text-[14px]">
            <span className="text-gray-600">Subtotal</span>
            <span>₦{formatNumber(orderGroup?.subTotalAmount)}</span>
          </div>
          <div className="flex justify-between text-[14px]">
            <span className="text-gray-600">Shipping Fee</span>
            <span>₦{formatNumber(orderGroup?.shippingAmount)}</span>
          </div>
          {orderGroup?.couponDiscount && (
            <div className="flex justify-between text-[14px]">
              <span className="text-gray-600">Coupon Discount</span>
              <span className="text-green-600">-₦{formatNumber(orderGroup?.couponDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between text-[16px] font-semibold pt-3 border-t">
            <span>Total</span>
            <span className="text-[#FF9D21]">₦{formatNumber(orderGroup?.totalAmount)}</span>
          </div>
        </div>
      </div>

      {orderGroup?.trackingUrl && (
        <div className="mt-6 text-center">
          <button
            onClick={() => window.open(orderGroup?.trackingUrl, '_blank')}
            className="bg-[#FF9D21] text-white px-6 py-2 rounded-full text-[14px] hover:bg-opacity-90 transition-all flex items-center gap-2 mx-auto"
          >
            <TbTruckDelivery className="text-xl" />
            Track Order
          </button>
        </div>
      )}
    </div>
  );
}

export default AccountOrderDetails;
